import PAQuickSearchManager from '//www.99bikes.co.nz/cdn/shop/t/817/assets/99-particular-audience-search-manager.js?manual=20261002&v=152575391836323177451792729155';

class PAPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.focusElement = this.input;
    this.resetButton?.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 600)); //99B 20250725 -PO, change from 300 to 600ms delay to reduce Algolia costs
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.hiddenCollections = (this.dataset.hiddenCollections || '')
      .split(',')
      .map(handle => handle.trim())
      .filter(Boolean);

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.paSearchManager = new PAQuickSearchManager();

    // Add form submission listener for recent searches
    const searchForm = this.querySelector('form');
    if (searchForm) {
      searchForm.addEventListener('submit', this.handleFormSubmission.bind(this));
    }

    this.isHeaderSearch = this.closest('header') !== null;

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove(this.states.OPEN);
      document.body.classList.remove(this.states.SEARCH_OPEN);
    });
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    //20260608 -PO, if clicking "clear" button, don't reopen the menu draw
    if (event.relatedTarget?.classList.contains('search__reset')) return;

    const menuDrawer = document.querySelector('#MenuDrawer');
    if (menuDrawer) {
      menuDrawer.hide();
    }

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add(this.states.SEARCH_OPEN);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }

    this.classList.add(this.states.OPEN);

    const query = this.getQuery();
    if (query.length === 0) {
      // Show recently viewed products when search is empty
      this.showRecentlyViewedProducts();
      return;
    }

    // Use Particular Audience search instead of Shopify API
    this.performPASearch(query);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      this.performPASearch(query);
    }
  }

  onChange() {
    const query = this.getQuery();
    if (query.length === 0) {
      this.showRecentlyViewedProducts();
      return;
    }
    this.performPASearch(query);
  }

  async performPASearch(query) {
    this.setLoadingState(true);
    const results = await this.paSearchManager.search(query,{},[],0,5,'searchPreview',false);
    if (!results) {
      console.error('PA quick search failed:', error);
      this.renderNoResults(query);
      this.setLoadingState(false);
      return;
    }

    const transformedResults = this.transformPAResults(results, query);
    this.renderSearchResults(transformedResults);

    this.setLoadingState(false);
  }

  async showRecentlyViewedProducts() {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (!targetElement) {
      console.error(`Element with id '${id}' not found in the document.`);
      return;
    }

    this.setLoadingState(true);

    try {
      const recentlyViewedProducts = await this.getRecentlyViewedProducts();

      //20260608 -PO, if query has been typed while awaiting, abandon
      if (this.getQuery().length > 0) return;

      const recentSearches = this.getRecentSearches();

      if (recentlyViewedProducts.length > 0 || recentSearches.length > 0) {
        targetElement.innerHTML = this.buildEmptySearchHTML(recentlyViewedProducts, recentSearches);
      } else {
        targetElement.innerHTML = this.buildEmptyStateHTML();
      }

      this.searchContent?.classList.remove('hidden');
      this.setAttribute('results', 'true');

      if (this.isHeaderSearch && !document.body.classList.contains(this.states.SEARCH_OPEN)) {
        document.body.classList.add(this.states.SEARCH_OPEN);
        this.classList.add(this.states.OPEN);
      }
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
      targetElement.innerHTML = this.buildEmptyStateHTML();
    } finally {
      this.setLoadingState(false);
    }
  }

  async getRecentlyViewedProducts() {
    const items = JSON.parse(
      window.localStorage.getItem('hypertheme:recently-viewed-handle') || '[]'
    );
    const limit = 5; // Show up to 5 recently viewed products

    if (items.length === 0) return [];

    try {
      // Use Shopify's Storefront API to fetch products by ID
      const products = await this.fetchProductsByIds(items.slice(0, limit));
      return products;
    } catch (error) {
      console.error('Error fetching recently viewed products:', error);
      return [];
    }
  }

  async fetchProductsByIds(productIds) {
    const products = [];

    // Fetch products in batches to avoid overwhelming the API
    const batchSize = 3;
    for (let i = 0; i < productIds.length; i += batchSize) {
      const batch = productIds.slice(i, i + batchSize);
      const batchPromises = batch.map(id => this.fetchSingleProduct(id));

      try {
        const batchResults = await Promise.allSettled(batchPromises);
        batchResults.forEach(result => {
          if (result.status === 'fulfilled' && result.value) {
            products.push(result.value);
          }
        });
      } catch (error) {
        console.error('Error fetching product batch:', error);
      }
    }

    return products;
  }

  async fetchSingleProduct(productId) {
    try {
      const response = await fetch(`/products/${productId}.js`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const productData = await response.json();

      // Transform featured image
      const featured_image = productData.featured_image
        ? {
            url: productData.featured_image.startsWith('//')
              ? `https:${productData.featured_image}`
              : productData.featured_image,
            alt: productData.title,
            width: 1500,
            height: 1500,
            aspect_ratio: 1.0,
          }
        : null;

      // Transform all images
      const images = productData.images.map(imageUrl => ({
        url: imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl,
        alt: productData.title,
        width: 1500,
        height: 1500,
        aspect_ratio: 1.0,
      }));

      // Transform variants
      const variants = productData.variants.map(variant => ({
        id: variant.id,
        title: variant.title,
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        option1: variant.option1,
        available: variant.available,
        metafields: {},
      }));

      return {
        id: productData.id,
        title: productData.title,
        vendor: productData.vendor || '',
        type: productData.type || '',
        url: `/products/${productData.handle}`,
        handle: productData.handle,
        featured_image: featured_image,
        images: images,
        variants: variants,
        available: productData.available,
        tags: productData.tags || [],
        has_only_default_variant: variants.length === 1,
        selected_or_first_available_variant: variants.find(v => v.available) || variants[0],
        price: productData.price,
        compare_at_price: productData.compare_at_price,
        price_min: productData.price_min,
        price_max: productData.price_max,
      };
    } catch (error) {
      console.error(`Error fetching product ${productId}:`, error);
      return null;
    }
  }

  formatPrice(price, compareAtPrice) {
    if (!price) return '';

    const formatMoney = cents => {
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    };

    const formattedPrice = formatMoney(price);

    if (compareAtPrice && compareAtPrice > price) {
      const formattedComparePrice = formatMoney(compareAtPrice);
      return `
        <span class="price price--on-sale">
          <span class="price__current">${formattedPrice}</span>
          <span class="price__compare">${formattedComparePrice}</span>
        </span>
      `;
    }

    return `<span class="price">${formattedPrice}</span>`;
  }

  saveRecentSearch(query) {
    if (!query || query.trim().length === 0) return;

    const searches = JSON.parse(window.localStorage.getItem('hypertheme:recent-searches') || '[]');
    const trimmedQuery = query.trim();

    // Remove if already exists
    const existingIndex = searches.indexOf(trimmedQuery);
    if (existingIndex > -1) {
      searches.splice(existingIndex, 1);
    }

    // Add to beginning
    searches.unshift(trimmedQuery);

    // Keep only 10 most recent
    const limitedSearches = searches.slice(0, 10);

    window.localStorage.setItem('hypertheme:recent-searches', JSON.stringify(limitedSearches));
  }

  getRecentSearches() {
    const searches = JSON.parse(window.localStorage.getItem('hypertheme:recent-searches') || '[]');
    return searches.slice(0, 5); // Return 5 most recent
  }

  buildEmptySearchHTML(recentlyViewedProducts, recentSearches) {
    return `
      <div class="flex w-full flex-col gap-y-6 md:flex-row">
        <div class="flex flex-col flex-grow search__box-products">
          ${
            recentlyViewedProducts.length > 0
              ? this.buildRecentlyViewedHTML(recentlyViewedProducts)
              : ''
          }
        </div>
        <div class="search__box-others flex flex-col gap-6 lg:gap-8${
          recentlyViewedProducts.length === 0 ? ' no-products' : ''
        }">
          ${recentSearches.length > 0 ? this.buildRecentSearchesHTML(recentSearches) : ''}
        </div>
      </div>
    `;
  }

  buildRecentlyViewedHTML(products) {
    // Products are already properly formatted from fetchSingleProduct, no need to transform again
    const productsHTML = products
      .map((product, index) => this.buildProductCardHTML(product, index + 1))
      .join('');

    return `
      <div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products">
        <h4 class="h4 predictive-search-result__heading">Recently Viewed</h4>
        <div class="swipe-mobile swipe-mobile--2-cols">
          <div id="predictive-search-results-recently-viewed-list" class="f-grid gap-3 lg:gap-7d5 swipe-mobile__inner">
            ${productsHTML}
          </div>
        </div>
      </div>
    `;
  }

  buildRecentSearchesHTML(searches) {
    const searchesHTML = searches
      .map(
        (search, index) => `
        <li class="predictive-search-result__list-item flex items-center justify-between" role="option" aria-selected="false">
          <button
            type="button"
            class="reversed-link inline-flex items-center gap-2 recent-search-btn flex-grow text-left"
            data-search="${search}"
            aria-label="Search for ${search}"
          >
            <span>${search}</span>
          </button>
          <button
            type="button"
            class="recent-search-clear-btn text-subtext hover:text-text transition-colors ml-2"
            data-search="${search}"
            aria-label="Remove ${search} from recent searches"
          >
            <svg class="icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </li>
      `
      )
      .join('');

    // Add event listeners after rendering
    setTimeout(() => {
      this.addEventListener('click', this.handleRecentSearchesClick);
    }, 0);

    return `
      <div class="grid predictive-search-result search__box-item predictive-search-result--recent-searches">
        <div class="flex items-center justify-between">
          <h4 class="h6 predictive-search-result__heading">Recent Searches</h4>
          <button
            type="button"
            class="recent-searches-clear-all-btn text-subtext hover:text-text transition-colors text-sm"
            aria-label="Clear all recent searches"
          >
            Clear all
          </button>
        </div>
        <ul class="predictive-search-result__list flex flex-col text-subtext gap-y-2">
          ${searchesHTML}
        </ul>
      </div>
    `;
  }

  //20260608 -PO, recent searches button actions to add when recent searches displayed
  handleRecentSearchesClick(e) {
    const searchBtn = e.target.closest('.recent-search-btn');
    if (searchBtn) {
      e.preventDefault();
      const searchTerm = searchBtn.dataset.search;
      this.input.value = searchTerm;
      this.performPASearch(searchTerm);
      return;
    }

    const clearBtn = e.target.closest('.recent-search-clear-btn');
    if (clearBtn) {
      e.preventDefault();
      e.stopPropagation();
      const searchTerm = clearBtn.dataset.search;
      this.clearSingleRecentSearch(searchTerm);
      return;
    }

    const clearAllBtn = e.target.closest('.recent-searches-clear-all-btn');
    if (clearAllBtn) {
      e.preventDefault();
      this.clearAllRecentSearches();
    }
  }

  buildEmptyStateHTML() {
    return `
      <div class="empty-state flex flex-col gap-3 items-center justify-center text-center w-full h-full">
        <svg class="icon icon-empty-state" width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="23" stroke="currentColor" stroke-width="2"/>
          <path d="M16 20h16M16 28h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <h4>Start typing to search</h4>
        <p class="m-0">Search for products, collections, and more</p>
      </div>
    `;
  }

  buildCacheKey(query) {
    const productType = this.searchProductTypes?.value || '';
    return `predictive-${query}-${productType}`;
  }

  buildSearchParams(query) {
    let searchQuery = query;

    // Add product type filter if selected
    if (this.searchProductTypes && this.searchProductTypes.value !== '') {
      searchQuery = `product_type:${this.searchProductTypes.value} AND ${query}`;
    }

    return {
      query: searchQuery,
      page: 0,
      filters: null, // Base inventory filter is handled in algoliaSearchManager
      facets: ['vendor', 'product_type', 'tags', 'collections'],
      hitsPerPage: parseInt(this.dataset.resultsLimit) || 5,
      distinct: 10,
    };
  }

  transformPAResults(paResults, originalQuery) {
    const products = this.transformPAProducts(paResults.results || []);
    const suggestions = this.paGenerateSuggestions(originalQuery, paResults);
    const collections = this.paExtractCollections(paResults.results || []);

    return {
      performed: true,
      terms: originalQuery,
      resources: {
        products: products.slice(0, parseInt(this.dataset.resultsLimit) || 5),
        queries: suggestions,
        collections: collections,
        articles: [], // Not typically available in product search
        pages: [], // Not typically available in product search
      },
    };
  }

  transformPAVariants(variants) {
    return variants.map(variant => ({
      id: this.minifyVariantId(variant.id),
      title: variant.title || 'Default Title',
      price: Number.parseFloat(variant.price) * 100,
      compare_at_price: variant.compareAtPrice ? Number.parseFloat(variant.compareAtPrice) * 100 : null,
      available: variant.availableForSale,
      option1: variant.title || 'Default Title'
    }));
  }

  transformPAProducts(results) {
    if (!results || results.length === 0) return [];

    return results.slice(0, 5).map((paResult,index) => {
      const variants = paResult.variants || [];
      const mainVariant = variants?.[0];

      //Issues consistently passing through product handle. Backup of breaking apart full link
      const fullUrl = paResult.link || '';
      const handle = paResult.handle || fullUrl.substring(fullUrl.lastIndexOf('/')+1);

      return {
        ref_id: paResult.ref_id, //used in tracking
        id: this.minifyVariantId(mainVariant?.id),
        title: paResult.title,
        vendor: paResult.brand,
        type: paResult.type,
        handle: handle,
        url: `/products/${handle}`,
        featured_image: this.transformImage(paResult.image_link,paResult.title),
        images: paResult.image_link ? [this.transformImage(paResult.image_link,paResult.title)] : [],
        variants: this.transformPAVariants(paResult.variants || []),
        available: paResult.variants?.some(v => v.availableForSale) || false,
        tags: paResult.tags || [],
        has_only_default_variant: mainVariant.title === 'Default Title',
        compare_at_price: mainVariant?.compareAtPrice ? Number.parseFloat(mainVariant?.compareAtPrice) * 100 : null,
        price_min: mainVariant?.price ? Number.parseFloat(mainVariant.price) * 100 : null,
        price_max: mainVariant?.price ? Number.parseFloat(mainVariant.price) * 100 : null
      };
    });
  }

  minifyVariantId(id) {
    if (typeof id !== 'string') return id;
    return id.replace('gid://shopify/ProductVariant/','');
  }

  transformImage(imageUrl, title) {
    return imageUrl
      ? {
          url: imageUrl,
          alt: title || '',
          width: 800,
          height: 600,
          aspect_ratio: 4 / 3,
        }
      : null;
  }

  paGenerateSuggestions(originalQuery, results) {
    const suggestions = [];
    const facets = results.aggregations || {};
    const brands = results.aggregations?.brand || [];
    const types = results.aggregations?.filter_cat || [];

    // Use '/search' as fallback if FoxTheme.routes.search_url is undefined
    const searchUrl = FoxTheme.routes?.search_url || '/search';

    // Generate suggestions based on facets that have results
    brands.slice(0,3).forEach(brand => {
      const brandName = brand.key;
      suggestions.push({
        text: `${originalQuery} ${brandName}`,
        styled_text: `<mark>${originalQuery}</mark> ${brandName}`,
        url: `${searchUrl}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(
          originalQuery + ' ' + brandName
        )}`,
      });
    });

    types.slice(0,2).forEach(type => {
      const typeName = type.key;
      suggestions.push({
        text: `${originalQuery} ${typeName}`,
        styled_text: `<mark>${originalQuery}</mark> ${typeName}`,
        url: `${searchUrl}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(
          originalQuery + ' ' + typeName
        )}`,
      });
    });

    return suggestions.slice(0, 5); // Limit to 5 suggestions
  }

  paGetSelectedVariant(variants) {
    const selectedVariant = variants.find(v => v.availableForSale) || variants[0];
    return {
      ...selectedVariant,
      price: Number.parseFloat(selectedVariant.price) * 100,
      compare_at_price: selectedVariant.compareAtPrice ? Number.parseFloat(selectedVariant.compareAtPrice) * 100 : null,
    }
  }

  paExtractCollections(results) {
    const collections = new Set();

    results.forEach(result => {
      const productCollections = result.collection_handles || [];
      productCollections.forEach(collection => {
        if (typeof collection === 'string' && !this.hiddenCollections.includes(collection)) {
          collections.add(collection);
        }
      });
    });

    return Array.from(collections)
      .slice(0, 4)
      .map(collectionHandle => ({
        title: this.formatCollectionTitle(collectionHandle),
        url: `/collections/${collectionHandle}`,
        handle: collectionHandle,
      }));
  }

  formatCollectionTitle(handle) {
    return handle
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  renderSearchResults(results) {
    //20260608 -PO, remove the recent searches buttons event listener
    this.removeEventListener('click', this.handleRecentSearchesClick)

    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (!targetElement) {
      console.error(`Element with id '${id}' not found in the document.`);
      return;
    }

    const hasResults =
      results.resources.products.length > 0 ||
      results.resources.queries.length > 0 ||
      results.resources.collections.length > 0;

    if (hasResults) {
      targetElement.innerHTML = this.buildResultsHTML(results);
    } else {
      targetElement.innerHTML = this.buildNoResultsHTML(results.terms);
    }

    this.searchContent?.classList.remove('hidden');
    this.setAttribute('results', 'true');

    if (this.isHeaderSearch && !document.body.classList.contains(this.states.SEARCH_OPEN)) {
      document.body.classList.add(this.states.SEARCH_OPEN);
      this.classList.add(this.states.OPEN);
    }
  }

  buildResultsHTML(results) {
    const { products, queries, collections } = results.resources;

    return `
      <div class="flex w-full flex-col gap-y-6 md:flex-row">
        <div class="flex flex-col flex-grow search__box-products">
          ${products.length > 0 ? this.buildProductsHTML(products, results.terms) : ''}
        </div>
        <div class="search__box-others flex flex-col gap-6 lg:gap-8${
          products.length === 0 ? ' no-products' : ''
        }">
          ${queries.length > 0 ? this.buildQueriesHTML(queries) : ''}
          ${collections.length > 0 ? this.buildCollectionsHTML(collections) : ''}
        </div>
      </div>
    `;
  }

  buildProductsHTML(products, searchTerms) {
    const productsHTML = products
      .map((product, index) => this.buildProductCardHTML(product, index + 1))
      .join('');

    return `
      <div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products">
        <h4 class="h4 predictive-search-result__heading">Results for "${searchTerms}"</h4>
        <div class="swipe-mobile swipe-mobile--2-cols">
          <div id="predictive-search-results-products-list" class="f-grid gap-3 lg:gap-7d5 swipe-mobile__inner"${this.paBuildWrapperAttributes(searchTerms)}>
            ${productsHTML}
          </div>
        </div>
        <div class="search__results-all block">
          <button type="submit" class="btn btn--primary" form="PredictiveSearch-${FoxTheme.utils.getSectionId(
            this
          )}">
            <span class="btn__text flex items-center gap-2">
              Search for "${searchTerms}"
              <svg class="icon icon-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M10 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </div>
    `;
  }

  paBuildWrapperAttributes(query) {
    try {
      const clientId = this.paSearchManager?.clientId;
      if (!clientId) throw new Error('PA clientId not found.');

      const key = `rec-${clientId}`;
      const recData = JSON.parse(localStorage.getItem(key) || null) || {};

      const routeKey = 'searchRouteId';
      const widgetKey = 'searchWidgetId';

      const routeId = recData?.[routeKey];
      const widgetId = recData?.[widgetKey];

      if (!routeId || !widgetId) throw new Error(`"${routeKey}" and/or "${widgetKey}" not found in rec data: ${JSON.stringify(recData)}.`);

      return ` data-pa-route-id="${routeId}" data-pa-widget-id="${widgetId}" data-pa-search-context="${query}"`;
    } catch(e) {
      console.error('Failed to add PA route and widget ids to quick search wrapper:',e);
      return '';
    }
  }

  buildProductCardHTML(product, index) {
    // Initialize product card renderer if not already done
    if (!window.productCardRenderer) {
      window.productCardRenderer = new ProductCardRenderer({
        showQuickView: false,
        showQuickAdd: false,
        cardStyle: 'standard',
        saleBadgeType: 'show_percentage',
        showVendor: false,
        showForm: false,
        showType: false,
        showPrice: true,
        contentAlignment: 'left',
      });
    }

    // Transform the product data to match the expected format
    const transformedProduct = {
      id: product.id,
      title: product.title,
      vendor: product.vendor || '',
      type: product.type || '',
      url: product.url,
      handle: product.handle,
      featured_image: product.featured_image,
      images: product.images || [],
      variants: product.variants.map(variant => ({
        id: variant.id,
        title: variant.title,
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        available: variant.available,
        metafields: variant.metafields || {},
      })),
      available: product.available,
      tags: product.tags || [],
      has_only_default_variant: product.variants.length === 1,
      selected_or_first_available_variant:
        product.variants.find(v => v.available) || product.variants[0],
      object_id: product.object_id,
      ref_id: product.ref_id
    };

    // Create wrapper div and render product card
    const card = document.createElement('div');
    card.classList.add('card');
    card.innerHTML = window.productCardRenderer.render(transformedProduct, {
      sectionId: 'predictive-search',
      customClass: index === 1 ? 'first' : '',
      //99B 20251016 -PO, adding algolia tracking information to predictive search product cards
      queryID: product.query_id,
      index: index
    });

    return card.innerHTML;
  }

  buildQueriesHTML(queries) {
    const queriesHTML = queries
      .map(
        (query, index) => `
      <li id="predictive-search-option-query-${index + 1}"
          class="predictive-search-result__list-item flex"
          role="option"
          aria-selected="false">
        <a href="${query.url}" class="reversed-link inline-flex items-center gap-2" aria-label="${
          query.text
        }">
          <span>${query.styled_text}</span>
        </a>
      </li>
    `
      )
      .join('');

    return `
      <div class="grid gap-3 predictive-search-result search__box-item predictive-search-result--queries">
        <h4 class="h6 predictive-search-result__heading">Suggestions</h4>
        <ul id="predictive-search-results-queries-list"
            role="group"
            aria-labelledby="predictive-search-queries"
            class="predictive-search-result__list flex flex-col text-subtext gap-y-2">
          ${queriesHTML}
        </ul>
      </div>
    `;
  }

  buildCollectionsHTML(collections) {
    const collectionsHTML = collections
      .map(
        (collection, index) => `
      <li id="predictive-search-option-collection-${index + 1}"
          class="predictive-search__list-item flex"
          role="option"
          aria-selected="false">
        <a href="${collection.url}" class="reversed-link">
          ${collection.title}
        </a>
      </li>
    `
      )
      .join('');

    return `
      <div class="grid gap-3 predictive-search-result search__box-item predictive-search-result--collections">
        <h4 class="h6 predictive-search-result__heading">Collections</h4>
        <ul id="predictive-search-results-collection-list"
            role="group"
            aria-labelledby="predictive-search-collection"
            class="predictive-search-result__list flex flex-col gap-y-3 text-subtext">
          ${collectionsHTML}
        </ul>
      </div>
    `;
  }

  buildNoResultsHTML(searchTerms) {
    return `
      <div class="empty-state flex flex-col gap-3 items-center justify-center text-center w-full h-full">
        <svg class="icon icon-empty-state" width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="23" stroke="currentColor" stroke-width="2"/>
          <path d="M16 20h16M16 28h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <h4>No results found for "${searchTerms}"</h4>
        <p class="m-0">Try different keywords or browse our collections</p>
      </div>
    `;
  }

  renderNoResults(searchTerms) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      targetElement.innerHTML = this.buildNoResultsHTML(searchTerms);
      this.searchContent?.classList.remove('hidden');
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton?.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton?.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }

  clearSingleRecentSearch(searchTerm) {
    const searches = JSON.parse(window.localStorage.getItem('hypertheme:recent-searches') || '[]');
    const updatedSearches = searches.filter(search => search !== searchTerm);
    window.localStorage.setItem('hypertheme:recent-searches', JSON.stringify(updatedSearches));

    // Refresh the display if we're currently showing recent searches
    if (this.getQuery().length === 0) {
      this.showRecentlyViewedProducts();
    }
  }

  clearAllRecentSearches() {
    window.localStorage.removeItem('hypertheme:recent-searches');

    // Refresh the display if we're currently showing recent searches
    if (this.getQuery().length === 0) {
      this.showRecentlyViewedProducts();
    }
  }

  handleFormSubmission(event) {
    const query = this.getQuery();
    if (query && query.trim().length > 0) {
      // Save to recent searches only on form submission
      this.saveRecentSearch(query.trim());
    }
  }
}

customElements.define('predictive-search', PAPredictiveSearch);